import './RecurringAtEditModal.scss'
import React, { useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { I18nReactContext } from '../../../contexts/I18nContext'
import { Modal } from '../../Smart/Modal/Modal'
import { Button } from '../../Form/Button/Button'
import { buttonColors } from '../../Pickers/InputTheme'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { he } from 'date-fns/locale'
import { updateSignDocRecurringAtAPI } from '../../../Services/api'
import Alert from '@mui/material/Alert'
import { RECURRING_SIGNDOC_ALLOW_PAST_DATE } from '../SignDocLib'

/**
 * Modal for editing the recurringAt (next version date) of a SEQUENTIAL SignDoc.
 * Follows the same pattern as PhoneEditModal — lightweight modal with a single field
 * that calls a dedicated API endpoint.
 *
 * @param {object} props - Component props
 * @param {string} props.signDocId - SignDoc ID to update
 * @param {string|null} props.currentRecurringAt - Current recurringAt date (ISO string or null)
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {function} props.onClose - Close handler
 * @param {function} props.onSuccess - Success callback after update
 */
export const RecurringAtEditModal = ({
    signDocId,
    currentRecurringAt,
    isOpen,
    onClose,
    onSuccess
}) => {
    const { i18n } = useContext(I18nReactContext)

    const [recurringAt, setRecurringAt] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    // Initialize state on modal open
    useEffect(() => {
        if (isOpen) {
            setRecurringAt(currentRecurringAt ? new Date(currentRecurringAt) : null)
            setError(null)
            setIsLoading(false)
        }
    }, [isOpen, currentRecurringAt])

    /**
     * Check if date has changed from original
     * @returns {boolean} True if date was modified
     */
    const hasChanged = () => {
        if (!recurringAt) return false
        if (!currentRecurringAt) return true
        return new Date(recurringAt).toDateString() !== new Date(currentRecurringAt).toDateString()
    }

    const isSaveDisabled = isLoading || !recurringAt || !hasChanged()

    /**
     * Handle save — calls dedicated recurringAt API endpoint
     */
    const handleSave = async () => {
        if (isSaveDisabled) return

        setIsLoading(true)
        setError(null)
        try {
            await updateSignDocRecurringAtAPI({ signDocId, recurringAt })
            onSuccess()
            onClose()
        } catch (err) {
            setError(i18n.translate('recurringAtEditModal.errors.saveFailed'))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
        >
            <div className="RecurringAtEditModal modalWrapper" data-testid="recurring-at-edit-modal">
                <div className="modalTitle" data-testid="recurring-at-edit-modal-title">
                    <div>{i18n.translate('recurringAtEditModal.title')}</div>
                </div>
                <div className="modalContent">
                    <Alert severity="info" className="RecurringAtEditModal__disclaimer">
                        {i18n.translate('recurringAtEditModal.disclaimer')}
                    </Alert>
                    <div className="RecurringAtEditModal__date-wrapper">
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
                            <DatePicker
                                value={recurringAt}
                                onChange={setRecurringAt}
                                disablePast={!RECURRING_SIGNDOC_ALLOW_PAST_DATE}
                                label={i18n.translate('recurringAtEditModal.dateLabel')}
                                data-testid="recurring-at-edit-date-picker"
                            />
                        </LocalizationProvider>
                    </div>
                    {error && (
                        <div className="RecurringAtEditModal__error" data-testid="recurring-at-edit-error">
                            {error}
                        </div>
                    )}
                </div>
                <div className="modalFooter">
                    <Button
                        color={buttonColors.YELLOW}
                        onClick={onClose}
                        disabled={isLoading}
                        text={i18n.translate('recurringAtEditModal.cancel')}
                        dataTestId="recurring-at-edit-cancel"
                    />
                    <Button
                        color={buttonColors.GREY}
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        loading={isLoading}
                        text={i18n.translate('recurringAtEditModal.save')}
                        dataTestId="recurring-at-edit-save"
                    />
                </div>
            </div>
        </Modal>
    )
}

RecurringAtEditModal.propTypes = {
    signDocId: PropTypes.string.isRequired,
    currentRecurringAt: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired
}
